<template>
  <div class="policy-shell">
    <div class="policy-wrap">
      <!-- ============ hero ============ -->
      <div class="policy-hero">
        <span class="hero-rail"></span>
        <div class="hero-badge"><v-icon size="24">mdi-shield-lock-outline</v-icon></div>
        <div class="hero-copy">
          <div class="hero-eyebrow">Sarpa Odisha</div>
          <h1 class="hero-title">Privacy Policy</h1>
          <p class="hero-lead">
            How the Sarpa app collects, uses and safeguards your information.
          </p>
        </div>
      </div>

      <!-- ============ location data ============ -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-crosshairs-gps</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Location Data</div>
            <div class="subtext">
              Including background location, which certain features require
            </div>
          </div>
        </div>

        <p class="lead-para">
          We collect location data from your device to provide and improve our
          services. This includes background location data necessary for certain
          features of the app.
        </p>

        <div class="term-grid">
          <div class="term-cell" v-for="(t, i) in locationTerms" :key="i">
            <div class="term-head">
              <span class="term-icon"><v-icon size="16">{{ t.icon }}</v-icon></span>
              <span class="term-name">{{ t.name }}</span>
            </div>
            <p class="term-text">{{ t.text }}</p>
          </div>
        </div>
      </div>

      <!-- ============ handling ============ -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-lock-check-outline</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Handling and Security</div>
            <div class="subtext">How your data is kept and who can reach it</div>
          </div>
        </div>
        <div class="term-grid">
          <div class="term-cell" v-for="(t, i) in handlingTerms" :key="i">
            <div class="term-head">
              <span class="term-icon"><v-icon size="16">{{ t.icon }}</v-icon></span>
              <span class="term-name">{{ t.name }}</span>
            </div>
            <p class="term-text">{{ t.text }}</p>
          </div>
        </div>
      </div>

      <!-- ============ contact ============ -->
      <div class="panel contact-panel">
        <div class="contact-copy">
          <div class="cardtitle">Questions about your data?</div>
          <div class="subtext">
            Write to us and we'll get back to you about anything on this page.
          </div>
        </div>
        <a class="contact-mail" :href="'mailto:' + supportEmail">
          <v-icon size="16">mdi-email-outline</v-icon>
          <span class="mail-text">{{ supportEmail }}</span>
        </a>
      </div>

      <p class="closing">
        Thank you for using Sarpa and being part of the solution to reduce
        snake&ndash;human conflicts. We appreciate your commitment to snake
        conservation and public safety.
      </p>
    </div>
  </div>
</template>

<script>
/*
 * Rewritten off <b-row>/<b-col>: bootstrap-vue is not a dependency here, so
 * those tags resolved to unknown elements with no grid behind them.
 *
 * The previous copy on this page was the Support page's text verbatim - app
 * features, how to get support, feedback, community guidelines - with the
 * location-data section appended at the end. Only that last part is actually
 * about privacy, so it is what this page now carries, reorganised. The wording
 * below is the original wording; nothing has been invented.
 */
export default {
  data() {
    return {
      supportEmail: "leopardtechlabs@gmail.com",
      locationTerms: [
        {
          icon: "mdi-map-marker-outline",
          name: "Type of data collected",
          text: "We collect GPS coordinates (latitude and longitude) to track your location while using the app. This allows us to provide accurate location-based services, such as tracking your movement during surveys.",
        },
        {
          icon: "mdi-target",
          name: "Purpose of collection",
          text: "The location data is used solely for survey-related purposes, including tracking your journey and logging distances travelled. This data is necessary for the functioning of certain app features, such as real-time location updates during surveys.",
        },
      ],
      handlingTerms: [
        {
          icon: "mdi-shield-check-outline",
          name: "Your safety and privacy",
          text: "Your safety and privacy are important to us. This policy sets out how we handle your data and keep your use of the app secure.",
        },
        {
          icon: "mdi-update",
          name: "Changes to this policy",
          text: "We are constantly improving the Sarpa app. Check back regularly so you stay informed about how this policy and the app change over time.",
        },
      ],
    };
  },
};
</script>

<style scoped>
/* ================= shell ================= */
.policy-shell {
  --accent: #10b981;
  --ink: #047857;
  --tint: #f0fdf9;
  --tint-line: #d2efe3;
  --badge: #d1fae5;
  --line: #e7eef3;
  --muted: #64748b;
  --shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -12px rgba(15, 23, 42, 0.1);

  background: #f6f8fb;
  padding: 28px 32px 40px 32px;
}
/*
 * The panels span the page like every other screen in the portal - a centred
 * column left wide empty gutters on a desktop monitor. Line length is held in
 * check on the prose itself (in ch, below) rather than by narrowing everything.
 */
.policy-wrap {
  width: 100%;
}

/* ================= typography ================= */
.cardtitle {
  font-family: poppinssemibold, poppinsregular, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1a2e23;
  letter-spacing: -0.1px;
}
.subtext {
  font-family: poppinsregular, sans-serif;
  font-size: 12.5px;
  color: var(--muted);
  line-height: 1.45;
}

/* ================= hero ================= */
.policy-hero {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 26px 26px 26px 30px;
  border: 1px solid var(--tint-line);
  border-radius: 18px;
  background: linear-gradient(135deg, #ffffff 0%, #f7fdfa 55%, #ecfdf5 100%);
  box-shadow: var(--shadow);
}
.hero-rail {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, var(--accent) 0%, var(--ink) 100%);
}
.hero-badge {
  flex: 0 0 auto;
  width: 50px;
  height: 50px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0ea97a 0%, var(--ink) 100%);
  box-shadow: 0 8px 20px -8px rgba(5, 150, 105, 0.55);
}
.hero-badge .v-icon {
  color: #ffffff !important;
}
.hero-copy {
  flex: 1 1 auto;
  min-width: 0;
}
.hero-eyebrow {
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: var(--ink);
}
.hero-title {
  margin: 3px 0 0 0;
  font-family: poppinssemibold, poppinsregular, sans-serif;
  font-size: 26px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.6px;
  color: #1a2e23;
}
.hero-lead {
  margin: 8px 0 0 0;
  max-width: 60ch;
  font-family: poppinsregular, sans-serif;
  font-size: 13.5px;
  line-height: 1.6;
  color: var(--muted);
}

/* ================= panels ================= */
.panel {
  margin-top: 20px;
  padding: 22px 24px 24px 24px;
  border: 1px solid var(--line);
  border-radius: 18px;
  background: #ffffff;
  box-shadow: var(--shadow);
}
.panel-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding-bottom: 16px;
  margin-bottom: 18px;
  border-bottom: 1px solid var(--line);
}
.panel-icon {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--badge);
}
.panel-icon .v-icon {
  color: var(--ink) !important;
}
.panel-copy {
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 2px;
}
.lead-para {
  /* the panel is full width now; hold the measure */
  max-width: 95ch;
  margin: 0 0 18px 0;
  font-family: poppinsregular, sans-serif;
  font-size: 13.5px;
  line-height: 1.65;
  color: #3f5c4e;
}

/* ================= terms ================= */
.term-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 12px;
}
.term-cell {
  min-width: 0;
  padding: 16px 18px;
  border: 1px solid var(--line);
  border-radius: 13px;
  background: #fcfefd;
  transition: border-color 0.25s ease, background 0.25s ease;
}
.term-cell:hover {
  border-color: var(--tint-line);
  background: var(--tint);
}
.term-head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.term-icon {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--tint-line);
  background: #ffffff;
}
.term-icon .v-icon {
  color: var(--ink) !important;
}
.term-name {
  font-family: poppinssemibold, poppinsregular, sans-serif;
  font-size: 13.5px;
  color: #1a2e23;
  letter-spacing: -0.1px;
}
.term-text {
  max-width: 80ch;
  margin: 10px 0 0 0;
  font-family: poppinsregular, sans-serif;
  font-size: 12.5px;
  line-height: 1.6;
  color: var(--muted);
}

/* ================= contact ================= */
.contact-panel {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.contact-copy {
  min-width: 0;
}
.contact-mail {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 999px;
  background: linear-gradient(135deg, #059669 0%, var(--ink) 100%);
  box-shadow: 0 8px 20px -8px rgba(5, 150, 105, 0.5);
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 12.5px;
  color: #ffffff !important;
  text-decoration: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.contact-mail .v-icon {
  color: #ffffff !important;
}
/* break between words, never mid-word: an address split as "…gmail.co / m"
   is worse than a slightly short line */
.mail-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.contact-mail:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 26px -10px rgba(5, 150, 105, 0.55);
}

/* ================= closing ================= */
.closing {
  max-width: 100ch;
  margin: 22px 0 0 0;
  padding: 16px 20px;
  border-left: 3px solid var(--accent);
  border-radius: 0 12px 12px 0;
  background: var(--tint);
  font-family: poppinsregular, sans-serif;
  font-size: 13px;
  line-height: 1.6;
  color: #3f5c4e;
}

@media (max-width: 600px) {
  .policy-shell {
    padding: 20px 16px 28px 16px;
  }
  .policy-hero {
    padding: 22px 18px 22px 22px;
  }
  .hero-title {
    font-size: 22px;
  }
  .panel {
    padding: 20px 16px 20px 16px;
  }
  .contact-mail {
    width: 100%;
    justify-content: center;
  }
}
</style>
